// nvRecruiter → Guide tab (owner, 2026-10-06: "Guide is supposed to be for
// nvRecruiter and its supposed to be an html page and guide menu item is
// supposed to be under nvRecruiter not up top in main menu"). The same text as
// the PDF Tomer attaches to email (docs/guides/nvrecruiter_guide.py, served at
// /guides/nvRecruiter-Guide.pdf) — change both together.
import type { JSX } from 'preact';

function Rows({ head, rows }: { head: string[]; rows: Array<Array<string | JSX.Element>> }): JSX.Element {
  return (
    <div class="table-wrap">
      <table class="data">
        <thead><tr>{head.map((h, i) => <th key={h} class={i === 0 ? 'name' : undefined}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={String(r[0])}>{r.map((c, i) => <td key={i} class={i === 0 ? 'name' : 'guide-text'}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RecruitingGuide(): JSX.Element {
  return (
    <section class="card recruiting-guide" data-testid="recruiting-guide">
      <div class="section-head-row">
        <h2>nvRecruiter: how it works</h2>
        <div class="section-head-actions">
          <a class="btn" href="/guides/nvRecruiter-Guide.pdf" target="_blank" rel="noopener noreferrer" data-testid="recruiting-guide-pdf">
            PDF to print or email
          </a>
        </div>
      </div>
      <p class="muted">
        nvRecruiter is where every <b>LMT (massage therapist) applicant</b> for your clinic lives: their resume, every
        application, and a log of every note, call, text, email and stage change. It replaces the Monday recruiting
        boards. Questions, or something looks wrong? Email Tomer.
      </p>

      <h3>1. Where to find it</h3>
      <ul>
        <li><b>Top menu: nvRecruiter.</b> The full board for your clinic (it opens on your clinic).</li>
        <li>
          <b>My Desk: the nvRecruiter table.</b> New applicants waiting for an email are listed at the top. Below them
          is one button per clinic showing <b>New today</b>, <b>To review</b> and <b>Interviews</b>. A clinic with
          someone new pulses green. Click a clinic's button to open its board.
        </li>
      </ul>

      <h3>2. How new applications arrive</h3>
      <p>LMT applications on our Indeed postings come in automatically. Nobody has to type them in.</p>
      <ul>
        <li>
          Indeed emails each application to nvManager. About once an hour it is read and an LMT applicant is added to
          <b> your clinic's board</b>, with their resume attached when Indeed sent one. Applications for other jobs
          (esthetician, front desk, manager) are not added.
        </li>
        <li>
          If they are already in nvRecruiter (same email, phone or resume), the new application is added to their
          existing record; no duplicate is created.
        </li>
        <li>You get an email: <b>"New application to review"</b>, with a link straight to their page.</li>
        <li>
          They show as an alert on your <b>My Desk nvRecruiter table</b> until someone <b>emails them from
          nvRecruiter</b> (or they are hired, turned down, or marked Do not contact). Press <b>Email</b> on the alert
          to open their page.
        </li>
      </ul>

      <h3>3. The board</h3>
      <p>
        <b>Clinic tabs</b> across the top: your clinic, <b>All clinics</b> (everyone in play), <b>Templates</b> (your
        email templates), <b>Archive</b> (people with an outcome, for checking before you call someone) and this
        <b> Guide</b>. <b>Add candidate</b> is for an LMT walk-in, a referral or anyone who did not come through
        Indeed. Next to <b>Board</b> at the very top is <b>Numbers</b> (section 9).
      </p>
      <p>
        <b>Day view:</b> New today, To review, Interviews, Offers out and LMTs on the roster. Click New today, To
        review, Interviews or Offers out to filter the list to just those people; click again to clear it.
      </p>
      <p>
        <b>Stage chips:</b> one per stage (Lead, To review, Phone screen, Interview, Practical, Offer, Hold, Hired),
        with how many people are in it. Click a chip to show only that stage. The <b>search box</b> finds anyone by
        name, email, phone or license number, across every LMT candidate who ever applied.
      </p>
      <Rows
        head={['Column', 'What it shows']}
        rows={[
          ['Candidate', 'Their name. Click it to open their page.'],
          ['Position', 'LMT.'],
          ['Stage', 'Where they are in your process (section 5).'],
          ['Since', 'The date they entered that stage.'],
          ['Last touch', 'The last time anyone logged a note, call, text, email or stage change.'],
          ['Source', 'Where they came from: Indeed, Monday (the old boards), walk-in, referral and so on.'],
          ['Received', 'When their application came in.'],
          ['Also applied', 'Other clinics they have applied to.'],
          ['Resume', 'Open the current resume ("Open (current)" when someone has marked one).'],
          ['Flags', <>
            <b>In process</b> (green: phone screen, interview, practical or offer), <b>Do not contact</b> (red),
            {' '}<b>Old employee</b>, <b>Resume missing</b>, <b>Quiet 7+</b> (nobody has touched them in 7+ days),
            {' '}<b>Applied before</b>.
          </>],
        ]}
      />

      <h3>4. A candidate's page</h3>
      <p>
        Click a name to open their page (<b>Back to all candidates</b> returns to the list). On the top line,
        <b> Stage</b> moves them on (pick the stage, write a short reason, Save; every move is logged) and
        <b> Outcome</b> closes them out (section 6), which moves them to the Archive.
      </p>
      <p>
        <b>Current resume.</b> Someone with several resumes on file? On the Profile tab press <b>Make current resume</b> on
        the right one; the list and the page then use it. <b>Use newest instead</b> undoes that. Files dated 07/29/2026
        are the day they moved over from Monday, not the resume's own date.
      </p>
      <Rows
        head={['Tab', 'What it does']}
        rows={[
          ['Profile', 'Name, email, phone, license, position, clinic, hiring manager, source, received date, time in stage and who referred them. Below that: every application, their files, and the resume right on the page.'],
          ['Notes', 'Log a Note, a Call or a Text: pick one, write what happened, Save. Below it is the Log: everything that ever happened with this person, newest first.'],
          ['Email', 'Write to them from your own nvManager address (section 7).'],
          ['License check', 'Record the Florida DOH license check you did (section 8).'],
        ]}
      />
      <Rows
        head={['Button', 'What it does']}
        rows={[
          ['Edit', 'Correct their details: name, email, phone, license number, position, clinic, hiring manager, address.'],
          ['Attach link', 'Add a file to their record by link (for example a resume or certificate in Drive).'],
          ['Merge', 'Combine two records that are the same person. Tomer and Candace only — the Same name tab lists every likely pair.'],
          ['Do not contact', 'Mark someone we must not contact, with a reason (section 6). Only Tomer can lift it.'],
          ['Referred by', 'Record the team member who referred them.'],
        ]}
      />

      <h3>5. Stages</h3>
      <Rows
        head={['Stage', 'Meaning']}
        rows={[
          ['Lead', 'Someone we know of who has not applied for a specific opening (mostly the older Monday records).'],
          ['To review', 'A new application waiting for you to look at it.'],
          ['Phone screen', 'You are calling or have called to screen them.'],
          ['Interview', 'An interview is set or done.'],
          ['Practical', 'A practical (hands-on) is set or done.'],
          ['Offer', 'An offer is out. Offers with no answer after 3 days show up in the morning reminder.'],
          ['Hold', 'Paused for now. The only stage allowed for a Do not contact person.'],
          ['Hired', 'Choosing Hired shows a Hire button. Pressing it records the hire and opens Manager Entry with the New hire line already filled in from their record.'],
        ]}
      />

      <h3>6. Outcomes and Do not contact</h3>
      <Rows
        head={['Outcome', 'Use it when']}
        rows={[
          ['Not eligible', 'They do not meet the requirements.'],
          ['Not yet – experience', 'Promising, but not enough experience yet. These come back for a second look later.'],
          ['Revisit', 'Worth another look in the future.'],
          ['Declined offer', 'We offered and they said no.'],
          ['Old employee', 'A former team member.'],
          ['Archive', 'Closed for any other reason.'],
          ['None – still active', 'Clears an outcome and puts them back in play.'],
        ]}
      />
      <p class="warn-note">
        <b>Do not contact is a wall.</b> A flagged person is red everywhere, cannot be emailed, moved forward or hired,
        and a new application from them is still caught, even under a different spelling. Only Tomer can lift it.
      </p>

      <h3>7. Emailing a candidate</h3>
      <p class="warn-note">
        <b>Email candidates from nvRecruiter, not from your Massage Envy address.</b> Every email you send from
        nvRecruiter, and every reply, is logged on the candidate's page so anyone can see where things stand.
        Email from your Massage Envy address is not tracked anywhere in nvManager.
      </p>
      <ul>
        <li>Open their page and choose the <b>Email</b> tab. It is sent from <b>your own nvManager email address</b>, so replies come back to you.</li>
        <li><b>To</b> is their email on file. If Indeed gave us only its relay address, the email goes through Indeed and still reaches them.</li>
        <li>Pick a <b>Template</b> to fill in the subject and message, then change anything you like before sending. Your own templates and the company ones are under <b>Templates</b>.</li>
        <li><b>Check Spelling/Grammar</b> tidies the message (only the message text is checked, nothing about the person).</li>
        <li><b>Send</b>. The email is saved on their page, and their replies are added to the log about once an hour.</li>
        <li>Emailing a new applicant takes them off your <b>new applicants</b> alert on My Desk.</li>
      </ul>

      <h3>8. License check</h3>
      <ul>
        <li>For someone you plan to interview, open the <b>License check</b> tab and use the link to the Florida Department of Health license search.</li>
        <li>Record what you found: <b>Result</b> (Clear / active, Expired or inactive, Not found, Discipline on file), the <b>expiry date</b> and a <b>note</b>. It is saved with your name and the date.</li>
        <li>nvRecruiter never checks a license by itself; it only records the check you did.</li>
      </ul>

      <h3>9. Numbers</h3>
      <p>
        <b>Numbers</b> (next to Board at the top) shows how recruiting is going for the last <b>30, 90 or 365
        days</b>, for your clinic or all clinics: hires by source, applications by source, how long people spend in
        each stage, outcomes with their reasons, and the total hires and terminations.
      </p>

      <h3>10. Reminders you will get</h3>
      <ul>
        <li><b>"New application to review"</b> as each Indeed application arrives (section 2).</li>
        <li>A <b>weekday morning email (8:00 AM)</b> listing anyone in your clinic nobody has touched in 48 hours, and offers with no answer after 3 days. People still untouched after 7 days are flagged to Tomer and Candace.</li>
      </ul>

      <h3>11. Who can do what</h3>
      <Rows
        head={['', 'Managers', 'Tomer & Candace']}
        rows={[
          ['See every clinic and the Archive', 'Yes', 'Yes'],
          ['Add, edit, move, note and email candidates', 'Your own clinic', 'Every clinic'],
          ['Merge two records', 'No', 'Yes'],
          ['Mark Do not contact', 'Yes', 'Yes'],
          ['Lift Do not contact', 'No', 'Tomer only'],
        ]}
      />

      <h3>12. A good daily routine</h3>
      <ul>
        <li>Open <b>My Desk</b>: anyone in the new applicants alert? Press <b>Email</b> and reply to each one.</li>
        <li>Open <b>nvRecruiter</b> and click <b>To review</b>: move each person to the next stage or set an outcome.</li>
        <li>After every call or text, log it on the <b>Notes</b> tab, so anyone can see where things stand.</li>
        <li>Keep an eye on <b>Quiet 7+</b> flags and <b>Offers out</b>.</li>
      </ul>
    </section>
  );
}
